import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#bf360c';
const PRIMARY_MID = '#e64a19';
const PRIMARY_LT  = '#fbe9e7';

const fmt     = (n) => { const v = parseFloat(n) || 0; return v === 0 ? '-' : `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const fmtQ    = (n) => { const v = parseFloat(n) || 0; return v === 0 ? '-' : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
const fmtPct  = (n) => { const v = parseFloat(n) || 0; return v === 0 ? '-' : `${v}%`; };
const fmtDate = (d) => {
    if (!d) return '-';
    const s = String(d).split('T')[0];
    const [y, m, day] = s.split('-');
    return `${day}/${m}/${y}`;
};
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const PurchaseAnalysisReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && branchId && token) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'PurchaseAnalysisReportApi',
                `/PurchaseAnalysisReport/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch purchase analysis report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.BillNumber    || '').toLowerCase().includes(q) ||
            (r.SupplierName  || '').toLowerCase().includes(q) ||
            (r.ItemName      || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => ({
        purchaseQty:      filtered.reduce((s, r) => s + (parseFloat(r.PurchaseQty)      || 0), 0),
        purchaseAmount:   filtered.reduce((s, r) => s + (parseFloat(r.PurchaseAmount)   || 0), 0),
        taxableAmount:    filtered.reduce((s, r) => s + (parseFloat(r.TaxableAmount)    || 0), 0),
        cgst:             filtered.reduce((s, r) => s + (parseFloat(r.CGST)             || 0), 0),
        sgst:             filtered.reduce((s, r) => s + (parseFloat(r.SGST)             || 0), 0),
        igst:             filtered.reduce((s, r) => s + (parseFloat(r.IGST)             || 0), 0),
        freightAmount:    filtered.reduce((s, r) => s + (parseFloat(r.FreightAmount)    || 0), 0),
        freightGSTAmount: filtered.reduce((s, r) => s + (parseFloat(r.FreightGSTAmount) || 0), 0),
        packingAmount:    filtered.reduce((s, r) => s + (parseFloat(r.PackingAmount)    || 0), 0),
        packingGSTAmount: filtered.reduce((s, r) => s + (parseFloat(r.PackingGSTAmount) || 0), 0),
        tcsAmount:        filtered.reduce((s, r) => s + (parseFloat(r.TCSAmount)        || 0), 0),
        discountAmount:   filtered.reduce((s, r) => s + (parseFloat(r.DiscountAmount)   || 0), 0),
        roundOff:         filtered.reduce((s, r) => s + (parseFloat(r.RoundOff)         || 0), 0),
        grossAmount:      filtered.reduce((s, r) => s + (parseFloat(r.GrossAmount)      || 0), 0),
    }), [filtered]);

    const uniqueBillCount = useMemo(() => {
        const ids = new Set(filtered.map(r => r.PurchaseId));
        return ids.size;
    }, [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'PurchaseAnalysisReport', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Bill No', 'Bill Date', 'Supplier', 'Item', 'Qty', 'Purchase Amt', 'Taxable', 'CGST', 'SGST', 'IGST', 'Freight Amt', 'Freight GST%', 'Freight GST Amt', 'Packing Amt', 'Packing GST%', 'Packing GST Amt', 'TCS Amt', 'Discount', 'Round Off', 'Gross Amt'];
        const rows = filtered.map((r, i) => [
            i + 1,
            r.BillNumber || '',
            fmtDate(r.BillDate),
            r.SupplierName || '',
            r.ItemName || '',
            parseFloat(r.PurchaseQty)      || 0,
            parseFloat(r.PurchaseAmount)   || 0,
            parseFloat(r.TaxableAmount)    || 0,
            parseFloat(r.CGST)             || 0,
            parseFloat(r.SGST)             || 0,
            parseFloat(r.IGST)             || 0,
            parseFloat(r.FreightAmount)    || 0,
            r.FreightGSTRate ? `${parseFloat(r.FreightGSTRate)}%` : '',
            parseFloat(r.FreightGSTAmount) || 0,
            parseFloat(r.PackingAmount)    || 0,
            r.PackingGSTRate ? `${parseFloat(r.PackingGSTRate)}%` : '',
            parseFloat(r.PackingGSTAmount) || 0,
            parseFloat(r.TCSAmount)        || 0,
            parseFloat(r.DiscountAmount)   || 0,
            parseFloat(r.RoundOff)         || 0,
            parseFloat(r.GrossAmount)      || 0,
        ]);
        const totalRow = ['', '', '', '', 'Totals',
            totals.purchaseQty, totals.purchaseAmount, totals.taxableAmount,
            totals.cgst, totals.sgst, totals.igst,
            totals.freightAmount, '', totals.freightGSTAmount,
            totals.packingAmount, '', totals.packingGSTAmount,
            totals.tcsAmount, totals.discountAmount, totals.roundOff,
            totals.taxableAmount + totals.cgst + totals.sgst + totals.igst + totals.freightAmount + totals.freightGSTAmount + totals.packingAmount + totals.packingGSTAmount + totals.tcsAmount - totals.discountAmount + totals.roundOff];

        await exportStyledExcel({
            reportTitle: 'PURCHASE ANALYSIS REPORT',
            sheetName: 'Purchase Analysis',
            fileName: `PurchaseAnalysisReport_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: totalRow,
            columnWidths: [4, 14, 12, 22, 28, 10, 14, 14, 12, 12, 12, 12, 12, 14, 12, 12, 14, 10, 12, 10, 14],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };
    const tdR    = { ...tdBase, textAlign: 'right' };

    // Sticky left column helpers
    const stickyTh = (left, extra = {}) => ({ ...thBase, position: 'sticky', left, zIndex: 3, background: PRIMARY_MID, ...extra });
    const stickyTd = (left, extra = {}) => ({ ...tdBase, position: 'sticky', left, zIndex: 1, ...extra });
    // left offsets: # = 0, BillNo = 36, BillDate = 156, Supplier = 246, Item = 406
    const COL = { slNo: 0, billNo: 36, billDate: 156, supplier: 246, item: 406 };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading purchase analysis report..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Purchase Analysis Report
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Item-wise Purchase Details</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input
                        placeholder="Search Bill / Supplier / Item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 200, outline: 'none' }}
                    />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary cards */}
                {fetched && data.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, padding: '5px 14px', borderBottom: '1px solid #f1f5f9', overflowX: 'auto', overflowY: 'hidden', flexWrap: 'nowrap', scrollbarWidth: 'none' }}>
                        {[
                            { label: 'Total Bills',    value: uniqueBillCount,                   color: PRIMARY },
                            { label: 'Total Qty',      value: fmtQ(totals.purchaseQty),      color: '#374151' },
                            { label: 'Purchase Amt',   value: fmt(totals.purchaseAmount),    color: '#0277bd' },
                            { label: 'Taxable',        value: fmt(totals.taxableAmount),     color: '#7b1fa2' },
                            { label: 'CGST',           value: fmt(totals.cgst),              color: '#c62828' },
                            { label: 'SGST',           value: fmt(totals.sgst),              color: '#c62828' },
                            { label: 'IGST',           value: fmt(totals.igst),              color: '#c62828' },
                            { label: 'Freight Amt',    value: fmt(totals.freightAmount),     color: '#e65100' },
                            { label: 'Freight GST',    value: fmt(totals.freightGSTAmount),  color: '#e65100' },
                            { label: 'Packing Amt',    value: fmt(totals.packingAmount),     color: '#6a1b9a' },
                            { label: 'Packing GST',    value: fmt(totals.packingGSTAmount),  color: '#6a1b9a' },
                            { label: 'TCS Amt',        value: fmt(totals.tcsAmount),         color: '#1565c0' },
                            { label: 'Discount',       value: fmt(totals.discountAmount),    color: '#558b2f' },
                            { label: 'Round Off',      value: fmt(totals.roundOff),          color: '#546e7a' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '3px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: 9, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{s.label}</span>
                                <span style={{ fontSize: 11, color: s.color, fontWeight: 700, whiteSpace: 'nowrap' }}>{s.value}</span>
                            </div>
                        ))}
                        {/* Gross Amount — calculated */}
                        <div style={{ background: '#2e7d32', borderRadius: 8, padding: '3px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, marginLeft: 4 }}>
                            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', fontWeight: 600, whiteSpace: 'nowrap' }}>Gross Amount</span>
                            <span style={{ fontSize: 11, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {`₹${(totals.taxableAmount + totals.cgst + totals.sgst + totals.igst + totals.freightAmount + totals.freightGSTAmount + totals.packingAmount + totals.packingGSTAmount + totals.tcsAmount - totals.discountAmount + totals.roundOff).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </span>
                        </div>
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {uniqueBillCount} items
                        {search && ` (filtered from ${new Set(data.map(r => r.PurchaseId)).size})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', scrollbarWidth: 'thin', scrollbarColor: `${PRIMARY} #f0f0f0` }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
                            <tr style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)` }}>
                                <th style={{ ...stickyTh(COL.slNo), textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...stickyTh(COL.billNo), minWidth: 120 }}>Bill No</th>
                                <th style={{ ...stickyTh(COL.billDate), minWidth: 90 }}>Bill Date</th>
                                <th style={{ ...stickyTh(COL.supplier), minWidth: 160 }}>Supplier</th>
                                <th style={{ ...stickyTh(COL.item), minWidth: 200, borderRight: '2px solid rgba(255,255,255,0.4)' }}>Item</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 80 }}>Qty</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>Purchase Amt</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>Taxable</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>CGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>SGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>IGST</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 100 }}>Freight Amt</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Freight GST%</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>Freight GST Amt</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 100 }}>Packing Amt</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Packing GST%</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110 }}>Packing GST Amt</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>TCS Amt</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Discount</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 90 }}>Round Off</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 110, background: '#2e7d32' }}>Gross Amt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={21} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : (() => {
                                // Group consecutive rows by PurchaseId
                                const groups = [];
                                filtered.forEach(r => {
                                    const last = groups[groups.length - 1];
                                    if (last && last.purchaseId === r.PurchaseId) {
                                        last.rows.push(r);
                                    } else {
                                        groups.push({ purchaseId: r.PurchaseId, rows: [r] });
                                    }
                                });

                                return groups.map((grp, gi) => {
                                    const bg = gi % 2 === 0 ? '#fff' : PRIMARY_LT;
                                    return grp.rows.map((r, ri) => {
                                        const isFirst = ri === 0;
                                        const span = grp.rows.length;
                                        return (
                                            <tr key={`${r.PurchaseId}-${r.ItemId}-${ri}`} style={{ background: bg }}>
                                                {isFirst && (
                                                    <>
                                                        <td rowSpan={span} style={{ ...stickyTd(COL.slNo, { textAlign: 'center', color: '#94a3b8', verticalAlign: 'middle', background: bg }) }}>{gi + 1}</td>
                                                        <td rowSpan={span} style={{ ...stickyTd(COL.billNo, { fontWeight: 600, color: PRIMARY, verticalAlign: 'middle', background: bg }) }}>{r.BillNumber || '-'}</td>
                                                        <td rowSpan={span} style={{ ...stickyTd(COL.billDate, { color: '#64748b', verticalAlign: 'middle', background: bg }) }}>{fmtDate(r.BillDate)}</td>
                                                        <td rowSpan={span} style={{ ...stickyTd(COL.supplier, { color: '#1e293b', verticalAlign: 'middle', background: bg }) }}>{r.SupplierName || '-'}</td>
                                                    </>
                                                )}
                                                <td style={{ ...stickyTd(COL.item, { color: '#374151', background: bg, borderRight: '2px solid #cbd5e1' }) }}>{r.ItemName || '-'}</td>
                                                <td style={{ ...tdR, color: '#374151' }}>{fmtQ(r.PurchaseQty)}</td>
                                                <td style={{ ...tdR, color: '#0277bd' }}>{fmt(r.PurchaseAmount)}</td>
                                                <td style={{ ...tdR, color: '#7b1fa2' }}>{fmt(r.TaxableAmount)}</td>
                                                <td style={{ ...tdR, color: '#c62828' }}>{fmt(r.CGST)}</td>
                                                <td style={{ ...tdR, color: '#c62828' }}>{fmt(r.SGST)}</td>
                                                <td style={{ ...tdR, color: '#c62828' }}>{fmt(r.IGST)}</td>
                                                <td style={{ ...tdR, color: '#e65100' }}>{fmt(r.FreightAmount)}</td>
                                                <td style={{ ...tdR, color: '#e65100' }}>{fmtPct(r.FreightGSTRate)}</td>
                                                <td style={{ ...tdR, color: '#e65100' }}>{fmt(r.FreightGSTAmount)}</td>
                                                <td style={{ ...tdR, color: '#6a1b9a' }}>{fmt(r.PackingAmount)}</td>
                                                <td style={{ ...tdR, color: '#6a1b9a' }}>{fmtPct(r.PackingGSTRate)}</td>
                                                <td style={{ ...tdR, color: '#6a1b9a' }}>{fmt(r.PackingGSTAmount)}</td>
                                                <td style={{ ...tdR, color: '#1565c0' }}>{fmt(r.TCSAmount)}</td>
                                                <td style={{ ...tdR, color: '#558b2f' }}>{fmt(r.DiscountAmount)}</td>
                                                <td style={{ ...tdR, color: '#546e7a' }}>{fmt(r.RoundOff)}</td>
                                                <td style={{ ...tdR, fontWeight: 700, color: '#2e7d32' }}>{fmt(r.GrossAmount)}</td>
                                            </tr>
                                        );
                                    });
                                });
                            })()}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td style={{ ...stickyTd(COL.slNo, { background: PRIMARY_LT, fontWeight: 700 }) }}></td>
                                    <td style={{ ...stickyTd(COL.billNo, { background: PRIMARY_LT, fontWeight: 700 }) }}></td>
                                    <td style={{ ...stickyTd(COL.billDate, { background: PRIMARY_LT, fontWeight: 700 }) }}></td>
                                    <td style={{ ...stickyTd(COL.supplier, { background: PRIMARY_LT, fontWeight: 700 }) }}></td>
                                    <td style={{ ...stickyTd(COL.item, { background: PRIMARY_LT, fontWeight: 700, color: PRIMARY, textAlign: 'right', borderRight: '2px solid #cbd5e1' }) }}>Totals</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#374151' }}>{fmtQ(totals.purchaseQty)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#0277bd' }}>{fmt(totals.purchaseAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#7b1fa2' }}>{fmt(totals.taxableAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.cgst)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.sgst)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.igst)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#e65100' }}>{fmt(totals.freightAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#e65100' }}></td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#e65100' }}>{fmt(totals.freightGSTAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#6a1b9a' }}>{fmt(totals.packingAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#6a1b9a' }}></td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#6a1b9a' }}>{fmt(totals.packingGSTAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#1565c0' }}>{fmt(totals.tcsAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#558b2f' }}>{fmt(totals.discountAmount)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#546e7a' }}>{fmt(totals.roundOff)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#2e7d32' }}>
                                        {(() => {
                                            const v = totals.taxableAmount + totals.cgst + totals.sgst + totals.igst
                                                + totals.freightAmount + totals.freightGSTAmount
                                                + totals.packingAmount + totals.packingGSTAmount
                                                + totals.tcsAmount - totals.discountAmount + totals.roundOff;
                                            return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                        })()}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

            </div>
        </div>
    );
};

export default PurchaseAnalysisReport;
